import React, {useMemo, useState} from "react";
import {Button, Row, Table} from "reactstrap";
import {useTranslation} from "react-i18next";
import moment from "moment/moment";
import QLoading from "../../../../common/components/QLoading";
import {LogInterface} from "../../../../interfaces/private/LogInterface";
import LogStatusBadge from "./LogStatusBadge";

interface Props {
    open: number | boolean,
    loading: boolean,
    setOpen: Function,
    setLoading: Function,
    log: LogInterface,
}

const formatPayload = (value: unknown) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "string") {
        try {
            return JSON.stringify(JSON.parse(value), null, 2);
        } catch {
            return value;
        }
    }

    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const BodyModalLogDetailComponent: React.FC<Props> = ({log, loading}) => {
    const {t} = useTranslation();
    const [copyState, setCopyState] = useState<"" | "copied" | "failed">("");
    const formattedData = useMemo(() => formatPayload(log.data), [log.data]);
    const formattedData1 = useMemo(() => formatPayload(log.data1), [log.data1]);
    const hasMatchingState = formattedData1 !== "";
    const referenceLabel = log.type === "payment"
        ? t("log.stripe_charge_id")
        : log.type === "payout"
            ? t("log.stripe_payout_id")
            : log.type === "subscription"
                ? t("log.stripe_invoice_id")
                : t("log.stripe_invoice_reference");

    const copyText = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopyState("copied");
        } catch {
            setCopyState("failed");
        }
        window.setTimeout(() => setCopyState(""), 1500);
    };

    const copyAll = () => copyText([
        `${referenceLabel}: ${log.ref_id}`,
        log.ref_id1 ? `${t("log.related_reference")}: ${log.ref_id1}` : "",
        `${t("log.status")}: ${log.status}`,
        `${t("log.message")}: ${log.message || "-"}`,
        hasMatchingState ? `${t("log.matching_state")}: ${formattedData1}` : "",
        `${t("log.data")}: ${formattedData || t("log.no_data")}`,
    ].filter(Boolean).join("\n"));

    const copyLabel = copyState === "copied"
        ? t("log.copied")
        : copyState === "failed"
            ? t("log.copy_failed")
            : t("log.copy_all");

    return (
        <React.Fragment>
            <QLoading loading={loading}/>
            {!loading && (
                <Row>
                    <div className="d-flex justify-content-end mb-2">
                        <Button color={copyState === "failed" ? "danger" : "light"} size="sm" type="button" onClick={copyAll}>
                            {copyLabel}
                        </Button>
                    </div>
                    <Table hover responsive>
                        <tbody>
                        <tr>
                            <th scope="row">{referenceLabel}</th>
                            <td>
                                <span className="text-break">{log.ref_id}</span>
                                <Button className="ms-2" color="link" size="sm" type="button" onClick={() => copyText(log.ref_id)}>
                                    {t("log.copy")}
                                </Button>
                            </td>
                        </tr>
                        {log.ref_id1 && (
                            <tr>
                                <th scope="row">{t("log.related_reference")}</th>
                                <td className="text-break">{log.ref_id1}</td>
                            </tr>
                        )}
                        <tr>
                            <th scope="row">{t("log.status")}</th>
                            <td><LogStatusBadge status={log.status} matchingStatus={log.data1}/></td>
                        </tr>
                        <tr>
                            <th scope="row">{t("log.message")}</th>
                            <td className="text-wrap">
                                {log.message || "-"}
                                {log.message && (
                                    <Button className="ms-2" color="link" size="sm" type="button" onClick={() => copyText(log.message)}>
                                        {t("log.copy")}
                                    </Button>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <th scope="row">{t("log.created_at")}</th>
                            <td>
                                {moment(log.created_at).isValid()
                                    ? moment(log.created_at).format("HH:mm:ss DD.MM.YY (Z)")
                                    : "-"}
                            </td>
                        </tr>
                        {hasMatchingState && (
                            <tr>
                                <th scope="row">{t("log.matching_state")}</th>
                                <td>{formattedData1}</td>
                            </tr>
                        )}
                        <tr>
                            <th scope="row">{t("log.data")}</th>
                            <td>
                                <div className="d-flex justify-content-end mb-2">
                                    <Button color="light" size="sm" type="button" onClick={() => copyText(formattedData)}>
                                        {t("log.copy")}
                                    </Button>
                                </div>
                                <pre className="bg-light p-3 mb-0 text-wrap" style={{maxHeight: "420px", overflow: "auto"}}>
                                    {formattedData || t("log.no_data")}
                                </pre>
                            </td>
                        </tr>
                        </tbody>
                    </Table>
                </Row>
            )}
        </React.Fragment>
    );
};

export default BodyModalLogDetailComponent;
